import { useContext, useEffect, useRef, useState } from 'react';

import { media } from 'context';
import { formatDate } from 'shared';
import { BLUE_500_HEX, BLUE_50_HEX, GRAY_50_HEX } from 'shared/colors';

import { Column, FilterState, Row } from './types';
import Button from '../Button';
import Clickable from '../Clickable';
import DatePicker from '../DatePicker/DatePicker';
import Icon from '../Icon';
import Layout from '../Layout';
import RadioButton from '../RadioButton';
import Text from '../Text';
import { useOnClickOutside } from '../helpers';

interface DateFilterProps<T extends Row> {
  filterState: FilterState;
  setFilterState: (filterState: FilterState | null) => void;
  column: Column<T>;
}

const DateFilter = <T extends Row>({ filterState, setFilterState, column }: DateFilterProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useContext(media);
  const [isOpen, setIsOpen] = useState(false);

  // Helper to normalize date (convert string to Date if needed)
  const normalizeDate = (date: Date | string | undefined | null): Date | null => {
    if (date === undefined || date === null) return null;
    if (date instanceof Date) return date;
    if (typeof date === 'string') return new Date(date);
    return null;
  };

  // Helper to compare two dates (handles both Date objects and strings)
  const datesEqual = (date1: Date | string | undefined | null, date2: Date | null): boolean => {
    const normalized1 = normalizeDate(date1);
    const normalized2 = normalizeDate(date2);
    if (normalized1 === null && normalized2 === null) return true;
    if (normalized1 === null || normalized2 === null) return false;
    return normalized1.getTime() === normalized2.getTime();
  };

  // Generate display text based on filterState
  const getDisplayText = (): string => {
    // Get the column header and format it
    const header = column.header.toLowerCase();
    const displayLabel = header.endsWith('date') ? column.header : `${column.header} date`;

    if (filterState.onDate !== undefined) {
      const date = normalizeDate(filterState.onDate);
      if (date) {
        return `${displayLabel}: ${formatDate({ date: date.toISOString(), format: 'SHORT' })}`;
      }
    }
    if (filterState.beforeDate !== undefined) {
      const date = normalizeDate(filterState.beforeDate);
      if (date) {
        return `${displayLabel}: before ${formatDate({ date: date.toISOString(), format: 'SHORT' })}`;
      }
    }
    if (filterState.afterDate !== undefined) {
      const date = normalizeDate(filterState.afterDate);
      if (date) {
        return `${displayLabel}: after ${formatDate({ date: date.toISOString(), format: 'SHORT' })}`;
      }
    }
    if (filterState.startDate !== undefined && filterState.endDate !== undefined) {
      const startDate = normalizeDate(filterState.startDate);
      const endDate = normalizeDate(filterState.endDate);
      if (startDate && endDate) {
        return `${displayLabel}: ${formatDate({ date: startDate.toISOString(), format: 'SHORT' })} – ${formatDate({
          date: endDate.toISOString(),
          format: 'SHORT'
        })}`;
      }
    }
    return `Any ${displayLabel}`;
  };

  // Determine which filter type is active in filterState
  const getActiveFilterType = (): 'onDate' | 'beforeDate' | 'afterDate' | 'between' | null => {
    if (filterState.onDate !== undefined) return 'onDate';
    if (filterState.beforeDate !== undefined) return 'beforeDate';
    if (filterState.afterDate !== undefined) return 'afterDate';
    if (filterState.startDate !== undefined || filterState.endDate !== undefined) return 'between';
    return null;
  };

  // Local state for filter type selection
  const [selectedFilterType, setSelectedFilterType] = useState<
    'onDate' | 'beforeDate' | 'afterDate' | 'between' | null
  >(getActiveFilterType());

  // Local state for dates (only updates filterState on Update click)
  // Normalize dates on initial mount in case they come from URL as strings
  const [localOnDate, setLocalOnDate] = useState<Date | null>(() => normalizeDate(filterState.onDate));
  const [localBeforeDate, setLocalBeforeDate] = useState<Date | null>(() => normalizeDate(filterState.beforeDate));
  const [localAfterDate, setLocalAfterDate] = useState<Date | null>(() => normalizeDate(filterState.afterDate));
  const [localStartDate, setLocalStartDate] = useState<Date | null>(() => normalizeDate(filterState.startDate));
  const [localEndDate, setLocalEndDate] = useState<Date | null>(() => normalizeDate(filterState.endDate));

  // Calendar element refs for click outside detection
  const [onDateCalendarElement, setOnDateCalendarElement] = useState<HTMLDivElement | null>(null);
  const [beforeDateCalendarElement, setBeforeDateCalendarElement] = useState<HTMLDivElement | null>(null);
  const [afterDateCalendarElement, setAfterDateCalendarElement] = useState<HTMLDivElement | null>(null);
  const [isStartDateRangeCalendarElement, setIsStartDateRangeCalendarElement] = useState<HTMLDivElement | null>(null);
  const [isEndDateRangeCalendarElement, setIsEndDateRangeCalendarElement] = useState<HTMLDivElement | null>(null);

  // Sync local state when filterState changes externally (e.g., from URL)
  useEffect(() => {
    const activeType =
      filterState.onDate !== undefined
        ? 'onDate'
        : filterState.beforeDate !== undefined
          ? 'beforeDate'
          : filterState.afterDate !== undefined
            ? 'afterDate'
            : filterState.startDate !== undefined || filterState.endDate !== undefined
              ? 'between'
              : null;
    setSelectedFilterType(activeType);
    setLocalOnDate(normalizeDate(filterState.onDate));
    setLocalBeforeDate(normalizeDate(filterState.beforeDate));
    setLocalAfterDate(normalizeDate(filterState.afterDate));
    setLocalStartDate(normalizeDate(filterState.startDate));
    setLocalEndDate(normalizeDate(filterState.endDate));
  }, [filterState]);

  // Check if there are changes between local state and filterState
  const hasChanges = (): boolean => {
    if (selectedFilterType === null) {
      // If no filter type selected, check if filterState has any date filters
      return (
        filterState.onDate !== undefined ||
        filterState.beforeDate !== undefined ||
        filterState.afterDate !== undefined ||
        filterState.startDate !== undefined ||
        filterState.endDate !== undefined
      );
    }

    if (selectedFilterType === 'onDate') {
      const datesMatch = datesEqual(filterState.onDate, localOnDate);
      return (
        !datesMatch ||
        filterState.beforeDate !== undefined ||
        filterState.afterDate !== undefined ||
        filterState.startDate !== undefined ||
        filterState.endDate !== undefined
      );
    }

    if (selectedFilterType === 'beforeDate') {
      const datesMatch = datesEqual(filterState.beforeDate, localBeforeDate);
      return (
        !datesMatch ||
        filterState.onDate !== undefined ||
        filterState.afterDate !== undefined ||
        filterState.startDate !== undefined ||
        filterState.endDate !== undefined
      );
    }

    if (selectedFilterType === 'afterDate') {
      const datesMatch = datesEqual(filterState.afterDate, localAfterDate);
      return (
        !datesMatch ||
        filterState.onDate !== undefined ||
        filterState.beforeDate !== undefined ||
        filterState.startDate !== undefined ||
        filterState.endDate !== undefined
      );
    }

    if (selectedFilterType === 'between') {
      const startMatch = datesEqual(filterState.startDate, localStartDate);
      const endMatch = datesEqual(filterState.endDate, localEndDate);
      return (
        !startMatch ||
        !endMatch ||
        filterState.onDate !== undefined ||
        filterState.beforeDate !== undefined ||
        filterState.afterDate !== undefined
      );
    }

    return false;
  };

  const handleUpdate = () => {
    // If no filter type selected or no valid dates, remove the filter completely
    if (
      selectedFilterType === null ||
      (selectedFilterType === 'onDate' && !localOnDate) ||
      (selectedFilterType === 'beforeDate' && !localBeforeDate) ||
      (selectedFilterType === 'afterDate' && !localAfterDate) ||
      (selectedFilterType === 'between' && (!localStartDate || !localEndDate))
    ) {
      setFilterState(null);
      setIsOpen(false);
      return;
    }

    const newFilterState: FilterState = {
      ...filterState,
      key: filterState.key,
      onDate: undefined,
      beforeDate: undefined,
      afterDate: undefined,
      startDate: undefined,
      endDate: undefined
    };

    if (selectedFilterType === 'onDate' && localOnDate) {
      newFilterState.onDate = localOnDate;
    } else if (selectedFilterType === 'beforeDate' && localBeforeDate) {
      newFilterState.beforeDate = localBeforeDate;
    } else if (selectedFilterType === 'afterDate' && localAfterDate) {
      newFilterState.afterDate = localAfterDate;
    } else if (selectedFilterType === 'between' && localStartDate && localEndDate) {
      newFilterState.startDate = localStartDate;
      newFilterState.endDate = localEndDate;
    }

    setFilterState(newFilterState);
    setIsOpen(false);
  };

  const handleClear = () => {
    setSelectedFilterType(null);
    setLocalOnDate(null);
    setLocalBeforeDate(null);
    setLocalAfterDate(null);
    setLocalStartDate(null);
    setLocalEndDate(null);
    // Pass null to remove the filter from the array
    setFilterState(null);
    setIsOpen(false);
  };

  useOnClickOutside(containerRef, event => {
    if (
      (onDateCalendarElement && onDateCalendarElement.contains(event.target as HTMLElement)) ||
      (beforeDateCalendarElement && beforeDateCalendarElement.contains(event.target as HTMLElement)) ||
      (afterDateCalendarElement && afterDateCalendarElement.contains(event.target as HTMLElement)) ||
      (isStartDateRangeCalendarElement && isStartDateRangeCalendarElement.contains(event.target as HTMLElement)) ||
      (isEndDateRangeCalendarElement && isEndDateRangeCalendarElement.contains(event.target as HTMLElement))
    ) {
      return;
    }
    setIsOpen(false);
  });

  const hasDateSelected = (): boolean => {
    const onDate = normalizeDate(filterState.onDate);
    const beforeDate = normalizeDate(filterState.beforeDate);
    const afterDate = normalizeDate(filterState.afterDate);
    const startDate = normalizeDate(filterState.startDate);
    const endDate = normalizeDate(filterState.endDate);

    return onDate !== null || beforeDate !== null || afterDate !== null || (startDate !== null && endDate !== null);
  };

  return (
    <Layout __ref={containerRef} position="relative" width={isMobile ? '100%' : 'fit-content'} borderRadius={8}>
      <button
        style={{
          paddingLeft: 10,
          paddingRight: 12,
          height: isMobile ? '48px' : '32px',
          borderRadius: '8px',
          border: isOpen || hasDateSelected() ? `1px solid ${BLUE_500_HEX}` : `1px solid ${GRAY_50_HEX}`,
          width: isMobile ? '100%' : undefined,
          backgroundColor: isOpen || hasDateSelected() ? BLUE_50_HEX : undefined
        }}
        onClick={() => setIsOpen(!isOpen)}
        className="hover-bg-color-gray-25"
      >
        <Layout direction="row" align="center" gap={isMobile ? 8 : 4} justify={isMobile ? 'space-between' : undefined}>
          <Text size="body-sm" wrap={false} color={`${isOpen || hasDateSelected() ? 'blue-500' : 'black'}`}>
            {getDisplayText()}
          </Text>
          {!hasDateSelected() && <Icon name="downCaret" size="xs" />}
        </Layout>
      </button>
      {isOpen && (
        <Layout
          background="white"
          position="absolute"
          color="white"
          paddingTop={12}
          paddingX={12}
          boxShadow="0px 8px 24px 0px rgba(0, 0, 0, 0.12)"
          borderRadius={8}
          width={isMobile ? '100%' : 359}
          top="100%"
          marginTop={4}
          zIndex={2000}
        >
          <Layout direction="column" gap={12}>
            <Layout direction="column" gap={12}>
              <RadioButton
                checked={selectedFilterType === 'onDate'}
                value="onDate"
                label="is on"
                onChange={() => setSelectedFilterType('onDate')}
                size="md"
              />
              {selectedFilterType === 'onDate' && (
                <Layout marginLeft={24}>
                  <DatePicker
                    selected={localOnDate}
                    onChange={date => setLocalOnDate(date)}
                    size="xxs"
                    setCalendarElement={setOnDateCalendarElement}
                    placeholder="Date"
                    allowPastDates
                  />
                </Layout>
              )}
              <RadioButton
                checked={selectedFilterType === 'beforeDate'}
                value="beforeDate"
                label="is before"
                onChange={() => setSelectedFilterType('beforeDate')}
                size="md"
              />
              {selectedFilterType === 'beforeDate' && (
                <Layout marginLeft={24}>
                  <DatePicker
                    selected={localBeforeDate}
                    onChange={date => setLocalBeforeDate(date)}
                    size="xxs"
                    setCalendarElement={setBeforeDateCalendarElement}
                    placeholder="Date"
                    allowPastDates
                  />
                </Layout>
              )}
              <RadioButton
                checked={selectedFilterType === 'afterDate'}
                value="afterDate"
                label="is after"
                onChange={() => setSelectedFilterType('afterDate')}
                size="md"
              />
              {selectedFilterType === 'afterDate' && (
                <Layout marginLeft={24}>
                  <DatePicker
                    selected={localAfterDate}
                    onChange={date => setLocalAfterDate(date)}
                    size="xxs"
                    setCalendarElement={setAfterDateCalendarElement}
                    placeholder="Date"
                    allowPastDates
                  />
                </Layout>
              )}
              <RadioButton
                checked={selectedFilterType === 'between'}
                value="isBetween"
                label="is in between"
                onChange={() => setSelectedFilterType('between')}
                size="md"
              />
              {selectedFilterType === 'between' && (
                <Layout marginLeft={24} direction="row" gap={4} align="center">
                  <Layout width={132} maxWidth={132}>
                    <DatePicker
                      selected={localStartDate}
                      onChange={date => setLocalStartDate(date)}
                      size="xxs"
                      setCalendarElement={setIsStartDateRangeCalendarElement}
                      placeholder="Start"
                      allowPastDates
                      maxDate={localEndDate}
                    />
                  </Layout>
                  <Text size="body-xs">–</Text>
                  <Layout width={132} maxWidth={132}>
                    <DatePicker
                      selected={localEndDate}
                      onChange={date => setLocalEndDate(date)}
                      size="xxs"
                      setCalendarElement={setIsEndDateRangeCalendarElement}
                      placeholder="End"
                      allowPastDates
                      minDate={localStartDate}
                    />
                  </Layout>
                </Layout>
              )}
            </Layout>
            <Layout
              height={48}
              paddingY={4}
              paddingX={36}
              direction="row"
              gap={8}
              justify="space-between"
              align="center"
            >
              <Clickable onClick={handleClear}>
                <Text size="body-sm" color="gray-700" semibold>
                  Clear selection
                </Text>
              </Clickable>
              <Button type="primary" text="Update" size="xxs" onClick={handleUpdate} disabled={!hasChanges()} />
            </Layout>
          </Layout>
        </Layout>
      )}
    </Layout>
  );
};

export default DateFilter;
